import styled from 'styled-components';

import useNow from 'hooks/useNow';
import { duration, elapsed, simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { isPortalWorkflow, PORTAL_DISCOVERY_API_NAME } from '../constants';
import { isOpenSweep } from '../referralProgress';
import { Tone, TONE_STYLES } from '../StatusBadge';

// The presentational pieces the referral inbox renders a run with, shared by the desktop table
// columns and the mobile card. They live here rather than in the columns file so the two renderers
// cannot drift: a status pill or a progress segment that means one thing in a row and another in a
// card is the kind of difference nobody notices until a screenshot sits beside a bug report.

// A discovery sweep is anchored to a portal, not a referral, so it has no patient and no
// insurance. It shares the inbox with retrieval runs (OLIO-2758), so the patient-shaped cells must
// not present a sweep as a referral with missing data.
export function isDiscovery(workflow: Workflow): boolean {
  return workflow.definition.apiName === PORTAL_DISCOVERY_API_NAME;
}

// What the subject cell says when there's no patient name. A sweep has no patient at all; a
// portal retrieval run has none *yet* (extraction fills it in later) and the bare 'New referral'
// fallback reads as a referral someone forgot to name.
//
// Deliberately NOT suffixed with the portal ("Portal referral — Faux-Port"): the table's Portal
// column right next door already says which portal, so the suffix was the same word twice per row.
// The detail page still suffixes, because it has no Portal column to lean on.
//
// Only for PORTAL runs: a referral-intake run genuinely is a new referral, so it keeps
// Workflow#patientName's wording.
export function subjectLabel(workflow: Workflow): string {
  if (workflow.hasPatientName) return workflow.patientName;
  // An open sweep pins to the top of the list (OLIO-2814); the suffix says why it's first so
  // the position doesn't read as an ordering quirk. Copy only — UX may replace it with a
  // dedicated affordance later. Drops with the pin once the sweep is handed off.
  if (isDiscovery(workflow)) return isOpenSweep(workflow) ? 'Portal sweep — needs review' : 'Portal sweep';
  if (!isPortalWorkflow(workflow.definition.apiName)) return workflow.patientName;
  return 'Portal referral';
}

// How long the referral has been open, ticking while it is. `decidedAt` freezes it: a referral with
// a recorded outcome already shows that outcome, so a clock still counting up beside it reads as
// unfinished work.
//
// Split out from ElapsedCell so the card can show the duration without the created-at line beneath
// it — the card has no column header to say what a bare date would mean, and the age is the part
// that is worth the space there.
export function ElapsedDuration({ created, decidedAt }: { created: Date | null; decidedAt?: Date | null }) {
  const now = useNow(30_000);
  if (!created) return <>—</>;
  return <Duration>{elapsed(created, decidedAt ?? now)}</Duration>;
}

export function ElapsedCell({ created, decidedAt }: { created: Date | null; decidedAt?: Date | null }) {
  if (!created) return <>—</>;
  return (
    <TimeCell>
      <ElapsedDuration created={created} decidedAt={decidedAt} />
      <Timestamp>{simpleDateAtTime(created)}</Timestamp>
    </TimeCell>
  );
}

// Awaiting Admission's Time Since Selection (OLIO-3176): how long the patient has been waiting in
// the SNF's queue, ticking like the age does, over the moment the selection was made. `selectedAt`
// is the live queue record's creation, so a moved winner restarts the clock — the honest reading.
//
// Duration only, same split as ElapsedDuration/ElapsedCell: the table pairs it with the timestamp
// beneath, the card shows the span alone.
export function SelectedAtDuration({ selectedAt }: { selectedAt?: Date | null }) {
  const now = useNow(30_000);
  if (!selectedAt) return <>—</>;
  return <Duration>{elapsed(selectedAt, now)}</Duration>;
}

// History's Time Spent: creation to the conclusion — a fixed span, so no clock. A row with no
// conclusion has nothing to measure and says so, rather than falling back to a ticking age under a
// heading that promises a finished figure.
//
// Shared by the table column and the card for the same reason as the two above: the file's whole
// point is that a card and a row cannot disagree.
export function TimeSpentDuration({ created, concluded }: { created: Date | null; concluded?: Date | null }) {
  if (!created || !concluded) return <>—</>;
  return <Duration>{duration(created, concluded)}</Duration>;
}

export const ProgressCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Bar = styled.div`
  display: flex;
  gap: 2px;
`;

// `$fluid`: the table gives every segment the same fixed 27px, because a column is a fixed width
// and a run with three steps should not draw a wider bar than one with eight. The card has the
// whole card width to fill instead, so there its segments share the row equally.
export const Segment = styled.div<{ $color: string; $fluid?: boolean }>`
  height: 9px;
  width: ${({ $fluid }) => ($fluid ? 'auto' : '27px')};
  flex: ${({ $fluid }) => ($fluid ? '1 1 0' : '0 0 auto')};
  min-width: ${({ $fluid }) => ($fluid ? '0' : 'auto')};
  flex-shrink: ${({ $fluid }) => ($fluid ? '1' : '0')};
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

export const ProgressLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

export const Pill = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].color};
`;

export const TimeCell = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Duration = styled.span`
  color: ${colors.black100};
`;

export const Timestamp = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;
